import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2, Loader2, Mail, Phone, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  full_name: "",
  company_name: "",
  email: "",
  phone: "",
  project_brief: "",
};

export default function Contact() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((err) => ({ ...err, [name]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Required";
    if (!form.company_name.trim()) e.company_name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Invalid email";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.project_brief.trim() || form.project_brief.trim().length < 10)
      e.project_brief = "Please share a few words about your project";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm(initial);
      toast.success("Brief received", {
        description: "We'll be in touch within 24 hours.",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong", {
        description: "Please try again in a moment.",
      });
    } finally {
      setLoading(false);
    }
  };

  const field = (name, placeholder, type = "text") => (
    <div className="relative">
      <input
        id={name}
        name={name}
        type={type}
        value={form[name]}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="off"
        className={`w-full bg-transparent border-b ${
          errors[name] ? "border-[#E02424]" : "border-white/15"
        } focus:border-[#60A5FA] outline-none text-white placeholder:text-white/35 py-3.5 text-base transition-colors`}
        data-testid={`contact-input-${name}`}
      />
      {errors[name] && (
        <span className="absolute -bottom-5 left-0 text-xs text-[#E02424]">
          {errors[name]}
        </span>
      )}
    </div>
  );

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-24 sm:py-32 px-5 sm:px-8 lg:px-12 bg-[#0A0F1E] overflow-hidden"
      data-testid="contact-section"
    >
      {/* background glow */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute left-1/2 top-0 -translate-x-1/2 w-[900px] h-[600px] blur-3xl opacity-40"
          style={{
            background:
              "radial-gradient(ellipse, rgba(26,86,219,0.35), transparent 60%)",
          }}
        />
        <div
          className="absolute right-10 bottom-0 w-[420px] h-[420px] blur-3xl opacity-30"
          style={{
            background:
              "radial-gradient(circle, rgba(224,36,36,0.35), transparent 70%)",
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
        className="relative max-w-5xl mx-auto"
      >
        <div className="text-center mb-12 sm:mb-16">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#60A5FA] font-semibold mb-4">
            <span className="inline-block h-px w-8 align-middle bg-[#60A5FA] mr-3" />
            Get in touch
            <span className="inline-block h-px w-8 align-middle bg-[#60A5FA] ml-3" />
          </div>
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05]"
            data-testid="contact-headline"
          >
            Ready to{" "}
            <span className="text-[#E02424]">Activate?</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-white/65 max-w-2xl mx-auto">
            Tell us about your brand and let's build something unforgettable.
          </p>
        </div>

        <div className="relative rounded-3xl border border-white/10 bg-[#1E2A4A]/60 backdrop-blur-xl p-6 sm:p-10 lg:p-12">
          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-16 text-center"
              data-testid="contact-success"
            >
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#1A56DB]/20 border border-[#60A5FA]/40 mb-6">
                <CheckCircle2 size={30} className="text-[#60A5FA]" />
              </div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">
                Brief received.
              </h3>
              <p className="mt-3 text-white/70 max-w-md mx-auto">
                We'll be in touch within 24 hours.
              </p>
              <button
                type="button"
                onClick={() => setSuccess(false)}
                className="mt-8 text-sm font-semibold text-[#60A5FA] hover:text-white transition-colors underline underline-offset-4"
                data-testid="contact-send-another"
              >
                Send another brief
              </button>
            </motion.div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-8" data-testid="contact-form" noValidate>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                {field("full_name", "Full name")}
                {field("company_name", "Company name")}
                {field("email", "Email address", "email")}
                {field("phone", "Phone number", "tel")}
              </div>

              <div className="relative">
                <textarea
                  id="project_brief"
                  name="project_brief"
                  rows={5}
                  value={form.project_brief}
                  onChange={onChange}
                  placeholder="Tell us about your project — brand, objective, timeline, budget..."
                  className={`w-full bg-transparent border-b ${
                    errors.project_brief ? "border-[#E02424]" : "border-white/15"
                  } focus:border-[#60A5FA] outline-none text-white placeholder:text-white/35 py-3.5 text-base resize-none transition-colors`}
                  data-testid="contact-input-project_brief"
                />
                {errors.project_brief && (
                  <span className="absolute -bottom-5 left-0 text-xs text-[#E02424]">
                    {errors.project_brief}
                  </span>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 pt-4">
                <p className="text-xs text-white/45 max-w-sm">
                  By sending this brief you agree to be contacted by our team.
                  We respond within 24 hours.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#E02424] px-8 py-4 text-sm font-semibold text-white hover:bg-[#c91f1f] disabled:opacity-70 disabled:cursor-not-allowed transition-all shadow-[0_20px_40px_-20px_rgba(224,36,36,0.9)]"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Brief
                      <Send
                        size={15}
                        className="transition-transform group-hover:translate-x-0.5"
                      />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Contact bits */}
        <div className="mt-10 grid sm:grid-cols-3 gap-4 text-sm">
          <a
            href="mailto:sales@axemarketing.in"
            className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 text-white/70 hover:text-white hover:border-[#60A5FA]/30 transition-colors"
            data-testid="contact-info-email"
          >
            <Mail size={16} className="text-[#60A5FA] shrink-0" />
            sales@axemarketing.in
          </a>
          <a
            href="tel:+919940167057"
            className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 text-white/70 hover:text-white hover:border-[#60A5FA]/30 transition-colors"
            data-testid="contact-info-phone"
          >
            <Phone size={16} className="text-[#60A5FA] shrink-0" />
            +91 99401 67057
          </a>
          <div
            className="flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 text-white/70 leading-snug"
            data-testid="contact-info-address"
          >
            <MapPin size={16} className="text-[#60A5FA] shrink-0 mt-0.5" />
            <span className="text-[13px]">
              G1, #4/608, V.O.C. Street, Desk - 107, Kottivakkam, Perungudi, OMR, Chennai, Tamil Nadu - 600041
            </span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
